'use client';

import debounce from 'lodash.debounce';
import { useAction } from 'next-safe-action/hooks';
import { useEffect, useState } from 'react';
import { useInterval } from '~/lib/use-interval';
import { actionRefreshToken } from './action-refresh-token';
export const REFRESH_TOKEN_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const debounceDelay = 500;

// Added this custom hook to monitor user interaction with the app.
// If the tab is open but the user doesn't interact with it for more than REFRESH_TOKEN_INTERVAL_MS,
// the `lastActivityDate` field will not be updated during the `actionRefreshToken` process,
// and the user will be marked as offline.
const useUserActivity = () => {
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    let activityTimeout: NodeJS.Timeout;
    const handleUserActivity = debounce(() => {
      // Mark user as active
      setIsActive(true);

      // Reset activity after a timeout
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => setIsActive(false), REFRESH_TOKEN_INTERVAL_MS);
    }, debounceDelay);
    const handleVisibilityChange = debounce(() => {
      if (document.visibilityState === 'visible') {
        handleUserActivity();
      }
    }, debounceDelay);
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Cancel debounced function
      handleUserActivity.cancel();
      handleVisibilityChange.cancel();
      clearTimeout(activityTimeout);
    };
  }, []);
  return isActive;
};

/**
 * The component calls a server action each N minutes to sign the user out if they've been deactivated, or update the permissions in the session token.
 */
export const RefreshTokenByInterval = () => {
  const $actionRefreshToken = useAction(actionRefreshToken);
  const isUserActive = useUserActivity();
  useEffect(() => {
    // updates the user's lastActivityDate when they enter the app
    $actionRefreshToken.execute({
      isUserActive: true
    });
  }, []);
  useInterval(() => {
    $actionRefreshToken.execute({
      isUserActive
    });
  }, REFRESH_TOKEN_INTERVAL_MS);
  return null;
};